var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showErrorToast, showSuccessToast } from '@ui';
import { translate } from '@utils/i18n';
import { bulkDestroyAttachments, bulkUpdateAttachmentCategory, bulkUpdateAttachmentTechVisibility, bulkUpdateAttachmentVisibility, destroyAttachment } from '../services/buildingAttachmentsApi';
import { buildingAttachmentsKeys } from './queryKeys';
const NS = 'features.building_attachments.mass_actions';
/**
 * Owns the three mass-update mutations (category, visibility, delete) shared between the toolbar's
 * bulk actions and the table's per-row delete icon — one `pendingDelete` state drives a single
 * `ConfirmDialog` instance for both E2E-32 (bulk) and E2E-33 (single row), rather than building two.
 *
 * Selection semantics (task-18 Step 4): this UI always sends the explicit checked-row `ids` array —
 * it never relies on task-15's "absent `ids` falls back to the current filtered/searched set"
 * server-side fallback (`AttachmentsController#resolved_attachments`). That fallback exists for
 * other future callers; this listing is unpaginated and fully client-side (see
 * `fetchBuildingAttachments`'s own doc comment), so there is no "select all matching filter across
 * pages" mode to support — every selection here is already the complete set of checked ids.
 */
export function useAttachmentMassActions(buildingId, selectedIds, clearSelection) {
    const queryClient = useQueryClient();
    const [pendingDelete, setPendingDelete] = useState(null);
    const invalidate = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: buildingAttachmentsKeys.list(buildingId) });
    }, [queryClient, buildingId]);
    const afterSuccess = useCallback(() => {
        clearSelection();
        invalidate();
    }, [clearSelection, invalidate]);
    // On failure: toast AND still invalidate, so the UI re-syncs with the server rather than
    // showing a stale state (MassActions.tsx:73-88 precedent) — selection is left as-is since
    // nothing here optimistically changed it.
    const afterError = useCallback((messageKey) => {
        showErrorToast(translate(messageKey, { namespace: NS }));
        invalidate();
    }, [invalidate]);
    const categoryMutation = useMutation({
        mutationFn: (categoryId) => bulkUpdateAttachmentCategory(buildingId, selectedIds, categoryId),
        onSuccess: () => {
            showSuccessToast(translate('toasts.category_success', { namespace: NS }));
            afterSuccess();
        },
        onError: () => afterError('toasts.category_error')
    });
    const visibilityMutation = useMutation({
        mutationFn: (showOnCustomerPortal) => bulkUpdateAttachmentVisibility(buildingId, selectedIds, showOnCustomerPortal),
        onSuccess: () => {
            showSuccessToast(translate('toasts.visibility_success', { namespace: NS }));
            afterSuccess();
        },
        onError: () => afterError('toasts.visibility_error')
    });
    // Bulk counterpart of the per-row `is_private` checkbox (task-17/task-23) — shares the
    // "Update visibility" popover and its success/error copy with `visibilityMutation` above, since
    // both checkboxes are part of the same action from the user's point of view.
    const techVisibilityMutation = useMutation({
        mutationFn: (isPrivate) => bulkUpdateAttachmentTechVisibility(buildingId, selectedIds, isPrivate),
        onSuccess: () => {
            showSuccessToast(translate('toasts.visibility_success', { namespace: NS }));
            afterSuccess();
        },
        onError: () => afterError('toasts.visibility_error')
    });
    const deleteMutation = useMutation({
        mutationFn: (target) => __awaiter(this, void 0, void 0, function* () {
            if (target.fileName) {
                yield destroyAttachment(buildingId, target.ids[0]);
                return;
            }
            yield bulkDestroyAttachments(buildingId, target.ids);
        }),
        onSuccess: (_data, target) => {
            showSuccessToast(translate('toasts.delete_success', { namespace: NS, count: target.ids.length }));
            setPendingDelete(null);
            afterSuccess();
        },
        onError: () => {
            setPendingDelete(null);
            afterError('toasts.delete_error');
        }
    });
    const updateCategory = useCallback((categoryId) => categoryMutation.mutate(categoryId), [categoryMutation]);
    const updateVisibility = useCallback((showOnCustomerPortal) => visibilityMutation.mutate(showOnCustomerPortal), [visibilityMutation]);
    const updateTechVisibility = useCallback((isPrivate) => techVisibilityMutation.mutate(isPrivate), [techVisibilityMutation]);
    const requestDelete = useCallback((ids, fileName) => setPendingDelete({ ids, fileName }), []);
    const cancelDelete = useCallback(() => setPendingDelete(null), []);
    const confirmDelete = useCallback(() => {
        if (pendingDelete)
            deleteMutation.mutate(pendingDelete);
    }, [pendingDelete, deleteMutation]);
    return {
        isMutating: categoryMutation.isLoading ||
            visibilityMutation.isLoading ||
            techVisibilityMutation.isLoading ||
            deleteMutation.isLoading,
        updateCategory,
        updateVisibility,
        updateTechVisibility,
        pendingDelete,
        requestDelete,
        cancelDelete,
        confirmDelete
    };
}
